import { Globe2 } from 'lucide-react';
import { motion } from 'motion/react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';

import { useLanguage, useTranslation } from '../i18n/hooks';

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage } = useLanguage();

  const t = useTranslation();

  const navItems = [
    { title: t.nav.aboutUs, id: 'about', isSection: true },
    { title: t.nav.services, id: 'services', isSection: true },
    { title: t.nav.specialties, id: 'specialties', isSection: true },
    { title: t.nav.doctors, id: '/our-doctors', isSection: false },
    { title: t.nav.faq, id: 'faq', isSection: true },
  ];

  const toggleLanguage = () => {
    setLanguage(language === 'ar' ? 'en' : 'ar');
  };

  return (
    <motion.header
      className={`fixed top-0 right-0 left-0 z-50 px-4 py-4 transition-colors duration-300 sm:px-6 sm:py-6 md:px-8 lg:px-32 lg:py-8 ${'border-b border-gray-100/50 bg-white/95 backdrop-blur-md'}`}
      initial={{ y: -100, opacity: 0 }}
      animate={{
        y: 0,
        opacity: 1,
      }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      <nav className="flex items-center justify-between">
        <div className="flex items-center gap-4 xl:gap-8">
          {/* Logo */}
          <motion.div
            className="flex items-center"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{
              delay: 0.3,
              duration: 0.5,
              type: 'spring',
              stiffness: 200,
            }}
          >
            <Link to="/">
              {language === 'ar' ? (
                <img
                  src="/images/takassusi-consult-02.png"
                  alt="Virtual Care Logo"
                  className="h-40 w-40 object-contain"
                />
              ) : (
                <img
                  src="/images/takassusi-consult-01.png"
                  alt="Virtual Care Logo"
                  className="h-20 w-20 object-contain"
                />
              )}
            </Link>
          </motion.div>
          {/* Desktop Navigation Menu */}
          <motion.div
            className="hidden items-center gap-4 lg:flex xl:gap-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            {navItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6 + index * 0.1, duration: 0.4 }}
              >
                {item.isSection ? (
                  <motion.a
                    href={
                      location.pathname === '/' ? `#${item.id}` : `/#${item.id}`
                    }
                    className="text-lg font-normal text-black transition-colors hover:text-green-600 xl:text-xl"
                    whileHover={{ scale: 1.05, color: '#16a34a' }}
                  >
                    {item.title}
                  </motion.a>
                ) : (
                  <Link to={item.id}>
                    <motion.span
                      className="text-lg font-normal text-black transition-colors hover:text-green-600 xl:text-xl"
                      whileHover={{ scale: 1.05, color: '#16a34a' }}
                    >
                      {item.title}
                    </motion.span>
                  </Link>
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Desktop Language Toggle & Auth Buttons */}
        <motion.div
          className="hidden items-center gap-3 md:gap-4 lg:flex lg:gap-6"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          {/* Language Toggle */}
          <motion.button
            onClick={toggleLanguage}
            className="flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-black transition-colors hover:bg-gray-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            aria-label={`Switch to ${language === 'ar' ? 'English' : 'Arabic'}`}
          >
            <Globe2 className="h-4 w-4" />
            <span className="font-medium">
              {language === 'ar' ? 'EN' : 'ع'}
            </span>
          </motion.button>

          <motion.button
            className="rounded-md border border-gray-300 px-2 py-2 text-sm text-black transition-colors hover:bg-gray-50 sm:px-3 md:px-4 md:text-base"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href="https://doctor.tc.tgl.com.sa/"
              target="_blank"
              rel="noreferrer"
            >
              {t.auth.doctorLogin}
            </a>
          </motion.button>
          <motion.button
            className="rounded-md bg-green-600 px-2 py-2 text-sm font-bold text-white transition-colors hover:bg-green-700 sm:px-3 md:px-4 md:text-base"
            whileHover={{ scale: 1.05, backgroundColor: '#15803d' }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href="https://patient.tc.tgl.com.sa/signin"
              target="_blank"
              rel="noreferrer"
            >
              {t.auth.patientLogin}
            </a>
          </motion.button>
        </motion.div>
        {/* Mobile Menu Button */}
        <motion.button
          className="flex flex-col gap-1 p-2 lg:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          whileTap={{ scale: 0.95 }}
        >
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? 'translate-y-1.5 rotate-45' : ''
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? 'opacity-0' : ''
            }`}
          ></span>
          <span
            className={`h-0.5 w-6 bg-black transition-all ${
              isMobileMenuOpen ? '-translate-y-1.5 -rotate-45' : ''
            }`}
          ></span>
        </motion.button>
      </nav>

      {/* Mobile Menu */}
      <motion.div
        className={`absolute top-full right-0 left-0 bg-white shadow-lg lg:hidden ${
          isMobileMenuOpen ? 'block' : 'hidden'
        }`}
        initial={{ opacity: 0, y: -20 }}
        animate={{
          opacity: isMobileMenuOpen ? 1 : 0,
          y: isMobileMenuOpen ? 0 : -20,
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col items-start gap-2.5 px-6 py-10">
          {/* Menu Items */}
          <div className="flex w-full flex-col items-start gap-8">
            {navItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{
                  x: isMobileMenuOpen ? 0 : -20,
                  opacity: isMobileMenuOpen ? 1 : 0,
                }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                className="w-full"
              >
                {item.isSection ? (
                  <motion.a
                    href={
                      location.pathname === '/' ? `#${item.id}` : `/#${item.id}`
                    }
                    className="block text-right text-xl font-normal text-black transition-colors hover:text-green-600"
                    onClick={() => setIsMobileMenuOpen(false)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {item.title}
                  </motion.a>
                ) : (
                  <Link to={item.id} onClick={() => setIsMobileMenuOpen(false)}>
                    <motion.span
                      className="block text-right text-xl font-normal text-black transition-colors hover:text-green-600"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.title}
                    </motion.span>
                  </Link>
                )}
              </motion.div>
            ))}
          </div>

          {/* Mobile Auth Buttons & Language Toggle */}
          <div className="mt-2 flex w-full flex-col gap-4 lg:hidden">
            {/* Language Toggle */}
            <motion.button
              onClick={toggleLanguage}
              className="flex h-11 w-full items-center justify-center gap-2 rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-center text-base font-normal text-black transition-colors hover:bg-gray-50"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
            >
              <Globe2 className="h-5 w-5" />
              <span className="font-medium">
                {language === 'ar' ? 'Switch to English' : 'التبديل للعربية'}
              </span>
            </motion.button>

            <motion.button
              className="h-11 w-full rounded-md border border-[#E5E5E5] bg-white px-4 py-2 text-center text-base font-normal text-black transition-colors hover:bg-gray-50"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
            >
              <a
                href="https://doctor.tc.tgl.com.sa/"
                target="_blank"
                rel="noreferrer"
              >
                {t.auth.doctorLogin}
              </a>
            </motion.button>
            <motion.button
              className="h-11 w-full rounded-md bg-[#2AB25F] px-3 py-2 text-center text-base font-bold text-[#FAFAFA] transition-colors hover:bg-[#229B4F]"
              whileTap={{ scale: 0.98 }}
              whileHover={{ scale: 1.02 }}
            >
              <a
                href="https://patient.tc.tgl.com.sa/signin"
                target="_blank"
                rel="noreferrer"
              >
                {t.auth.patientLogin}
              </a>
            </motion.button>
          </div>
        </div>
      </motion.div>
    </motion.header>
  );
}
